
import React, { useState } from "react";
import { 
  Tabs, 
  TabsContent, 
  TabsList, 
  TabsTrigger 
} from "@/components/ui/tabs";
import { 
  Table, 
  TableBody, 
  TableCaption, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { 
  ForecastModel, 
  ProjectionItem, 
  CashFlowItem 
} from "@/lib/types";
import { formatCurrency } from "@/lib/formatters";
import { ArrowLeft, Download, Table2 } from "lucide-react";

export interface DataSource {
  type: string;
  id: string;
  name: string;
  sourceType: string;
  data: any;
}

interface DataTablesProps {
  dataSource?: DataSource | null;
  onBack?: () => void;
  forecastModel?: ForecastModel;
  cashFlowData?: CashFlowItem[];
  projectId?: string;
}

const DataTables: React.FC<DataTablesProps> = ({ 
  dataSource, 
  onBack,
  forecastModel: propsForecastModel,
  cashFlowData: propsCashFlowData,
  projectId
}) => {
  const [activeTab, setActiveTab] = useState("profit-loss");

  // Helper function to export data as CSV
  const exportToCSV = (data: any[], filename: string) => {
    if (!data || data.length === 0) return;
    
    // Convert object keys to CSV headers
    const headers = Object.keys(data[0]);
    const csvRows = [headers.join(',')];

    // Convert data to CSV rows
    data.forEach(row => {
      const values = headers.map(header => {
        const value = row[header];
        // Handle different data types and ensure proper CSV formatting
        return typeof value === 'string' ? `"${value.replace(/"/g, '""')}"` : value;
      });
      csvRows.push(values.join(','));
    });

    // Create and download CSV file
    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `${filename}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Mock data generator
  const getMockData = () => {
    const forecastModel: ForecastModel = {
      id: "mock-id",
      name: "Sample Forecast Model",
      tenementId: "sample-tenement",
      startYear: 2023,
      endYear: 2027,
      mineralPrice: 1500,
      productionRampUp: [0.1, 0.2, 0.3, 0.3, 0.1],
      operatingCosts: [500000, 750000, 1000000, 1000000, 800000],
      capitalCosts: [2000000, 1000000, 500000, 200000, 100000],
      discountRate: 0.1,
      projectionData: [
        { year: 2023, production: 10000, revenue: 15000000, costs: 500000, cashFlow: 14500000, discountedCashFlow: 13181818, npv: 13181818 },
        { year: 2024, production: 20000, revenue: 30000000, costs: 750000, cashFlow: 29250000, discountedCashFlow: 24173554, npv: 37355372 },
        { year: 2025, production: 30000, revenue: 45000000, costs: 1000000, cashFlow: 44000000, discountedCashFlow: 33057851, npv: 70413223 },
        { year: 2026, production: 30000, revenue: 45000000, costs: 1000000, cashFlow: 44000000, discountedCashFlow: 30052592, npv: 100465815 },
        { year: 2027, production: 10000, revenue: 15000000, costs: 800000, cashFlow: 14200000, discountedCashFlow: 8819121, npv: 109284936 },
      ],
      isPreRevenue: false,
      budgetDetails: {
        keyExpensesBreakdown: {
          "Labor": 1200000,
          "Equipment": 800000,
          "Maintenance": 500000,
          "Energy": 300000,
          "Materials": 250000,
          "Permits": 100000,
          "Insurance": 80000,
          "Other": 170000
        },
        capitalExpenditureDetails: {
          "Infrastructure": 1200000,
          "Processing Plant": 1500000,
          "Mining Equipment": 800000,
          "Environmental Controls": 200000,
          "Exploration": 100000
        },
        contingencyReserve: 500000
      },
      assumptions: {
        mineralPriceProjection: [1500, 1550, 1600, 1650, 1700],
        inflationRate: 0.025,
        exchangeRate: 0.72,
        recoveryEfficiency: 0.92
      }
    };

    const cashFlowData: CashFlowItem[] = [
      { year: 2023, revenue: 15000000, costs: 500000, cashFlow: 14500000, discountedCashFlow: 13181818, cumulativeCashFlow: 14500000, cumulativeDiscountedCashFlow: 13181818 },
      { year: 2024, revenue: 30000000, costs: 750000, cashFlow: 29250000, discountedCashFlow: 24173554, cumulativeCashFlow: 43750000, cumulativeDiscountedCashFlow: 37355372 },
      { year: 2025, revenue: 45000000, costs: 1000000, cashFlow: 44000000, discountedCashFlow: 33057851, cumulativeCashFlow: 87750000, cumulativeDiscountedCashFlow: 70413223 },
      { year: 2026, revenue: 45000000, costs: 1000000, cashFlow: 44000000, discountedCashFlow: 30052592, cumulativeCashFlow: 131750000, cumulativeDiscountedCashFlow: 100465815 },
      { year: 2027, revenue: 15000000, costs: 800000, cashFlow: 14200000, discountedCashFlow: 8819121, cumulativeCashFlow: 145950000, cumulativeDiscountedCashFlow: 109284936 },
    ];

    return { forecastModel, cashFlowData };
  };

  // Use props data if available, otherwise use mock data
  const mockData = getMockData();
  const forecastModel = propsForecastModel || mockData.forecastModel;
  const cashFlowData = propsCashFlowData || mockData.cashFlowData;

  const renderProfitLossTable = () => {
    if (!forecastModel?.projectionData) return <p>No profit and loss data available</p>;
    
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Profit & Loss Statement</CardTitle>
            <CardDescription>
              Annual profit and loss projections over the forecast period
            </CardDescription>
          </div>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => exportToCSV(forecastModel.projectionData, 'profit_loss_statement')}
          >
            <Download className="h-4 w-4 mr-2" /> Export CSV
          </Button>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Year</TableHead>
                <TableHead>Revenue</TableHead>
                <TableHead>Operating Costs</TableHead>
                <TableHead>Net Profit/Loss</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {forecastModel.projectionData.map((item: ProjectionItem) => (
                <TableRow key={item.year}>
                  <TableCell>{item.year}</TableCell>
                  <TableCell>{formatCurrency(item.revenue)}</TableCell>
                  <TableCell>{formatCurrency(item.costs)}</TableCell>
                  <TableCell className={item.revenue - item.costs >= 0 ? "text-green-600" : "text-red-600"}>
                    {formatCurrency(item.revenue - item.costs)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    );
  };

  const renderCashFlowTable = () => {
    if (!cashFlowData) return <p>No cash flow data available</p>;
    
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Cash Flow Statement</CardTitle>
            <CardDescription>
              Detailed cash flow projections over time
            </CardDescription>
          </div>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => exportToCSV(cashFlowData, 'cash_flow_statement')}
          >
            <Download className="h-4 w-4 mr-2" /> Export CSV
          </Button>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Year</TableHead>
                <TableHead>Cash Flow</TableHead>
                <TableHead>Discounted Cash Flow</TableHead>
                <TableHead>Cumulative Cash Flow</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {cashFlowData.map((item: CashFlowItem) => (
                <TableRow key={item.year}>
                  <TableCell>{item.year}</TableCell>
                  <TableCell>{formatCurrency(item.cashFlow)}</TableCell>
                  <TableCell>{formatCurrency(item.discountedCashFlow)}</TableCell>
                  <TableCell>{formatCurrency(item.cumulativeCashFlow || 0)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    );
  };

  const renderOperationalCostsTable = () => {
    if (!forecastModel?.budgetDetails?.keyExpensesBreakdown) 
      return <p>No operational cost breakdown available</p>;

    const operationalCosts = forecastModel.budgetDetails.keyExpensesBreakdown;
    const costData = Object.entries(operationalCosts).map(([category, amount]) => ({
      category,
      amount
    }));
    
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Operational Cost Breakdown</CardTitle>
            <CardDescription>
              Detailed breakdown of key operational expenses
            </CardDescription>
          </div>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => exportToCSV(costData, 'operational_costs')}
          >
            <Download className="h-4 w-4 mr-2" /> Export CSV
          </Button>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Expense Category</TableHead>
                <TableHead>Amount</TableHead>
                <TableHead>% of Total</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {Object.entries(operationalCosts).map(([category, amount], index) => {
                const total = Object.values(operationalCosts).reduce((sum, val) => sum + Number(val), 0);
                const percentage = (Number(amount) / total * 100).toFixed(2);
                
                return (
                  <TableRow key={index}>
                    <TableCell>{category}</TableCell>
                    <TableCell>{formatCurrency(Number(amount))}</TableCell>
                    <TableCell>{percentage}%</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
            <TableCaption>
              Total Operational Costs: {formatCurrency(Object.values(operationalCosts).reduce((sum, val) => sum + Number(val), 0))}
            </TableCaption>
          </Table>
        </CardContent>
      </Card>
    );
  };

  const renderCapExTable = () => {
    if (!forecastModel?.budgetDetails?.capitalExpenditureDetails) 
      return <p>No capital expenditure data available</p>;

    const capExData = forecastModel.budgetDetails.capitalExpenditureDetails;
    const formattedData = Object.entries(capExData).map(([category, amount]) => ({
      category,
      amount
    }));
    
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Capital Expenditure</CardTitle>
            <CardDescription>
              Breakdown of capital expenditure by category
            </CardDescription>
          </div>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => exportToCSV(formattedData, 'capital_expenditure')}
          >
            <Download className="h-4 w-4 mr-2" /> Export CSV
          </Button>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Category</TableHead>
                <TableHead>Amount</TableHead>
                <TableHead>% of Total</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {Object.entries(capExData).map(([category, amount], index) => {
                const total = Object.values(capExData).reduce((sum, val) => sum + Number(val), 0);
                const percentage = (Number(amount) / total * 100).toFixed(2);
                
                return (
                  <TableRow key={index}>
                    <TableCell>{category}</TableCell>
                    <TableCell>{formatCurrency(Number(amount))}</TableCell>
                    <TableCell>{percentage}%</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
            <TableCaption>
              Total Capital Expenditure: {formatCurrency(Object.values(capExData).reduce((sum, val) => sum + Number(val), 0))}
            </TableCaption>
          </Table>
        </CardContent>
      </Card>
    );
  };

  const renderBudgetForecastTable = () => {
    if (!forecastModel?.projectionData) return <p>No budget and forecast data available</p>;
    
    // Create a structured budget and forecast table
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Budget & Forecast</CardTitle>
            <CardDescription>
              Detailed budget and forecast projections for the project
            </CardDescription>
          </div>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => exportToCSV(forecastModel.projectionData, 'budget_forecast')}
          >
            <Download className="h-4 w-4 mr-2" /> Export CSV
          </Button>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Year</TableHead>
                <TableHead>Production</TableHead>
                <TableHead>Revenue</TableHead>
                <TableHead>Operating Costs</TableHead>
                <TableHead>Capital Costs</TableHead>
                <TableHead>Net Cash Flow</TableHead>
                <TableHead>NPV</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {forecastModel.projectionData.map((item: ProjectionItem, index) => (
                <TableRow key={item.year}>
                  <TableCell>{item.year}</TableCell>
                  <TableCell>{item.production?.toFixed(2) || "N/A"}</TableCell>
                  <TableCell>{formatCurrency(item.revenue)}</TableCell>
                  <TableCell>{formatCurrency(item.costs)}</TableCell>
                  <TableCell>{formatCurrency(forecastModel.capitalCosts[index] || 0)}</TableCell>
                  <TableCell>{formatCurrency(item.cashFlow)}</TableCell>
                  <TableCell>{formatCurrency(item.npv)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {forecastModel.budgetDetails?.contingencyReserve && (
            <div className="mt-4 p-3 bg-slate-50 dark:bg-slate-800 rounded-md">
              <p className="font-medium">Contingency Reserve: {formatCurrency(forecastModel.budgetDetails.contingencyReserve)}</p>
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <Button variant="ghost" size="sm" onClick={onBack}>
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </Button>
          <h2 className="text-3xl font-bold tracking-tight">Data Tables</h2>
        </div>
      </div>
      
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid grid-cols-5 mb-6">
          <TabsTrigger value="profit-loss">Profit & Loss</TabsTrigger>
          <TabsTrigger value="cash-flow">Cash Flow</TabsTrigger>
          <TabsTrigger value="operational-costs">Operational Costs</TabsTrigger>
          <TabsTrigger value="capex">Capital Expenditure</TabsTrigger>
          <TabsTrigger value="budget-forecast">Budget & Forecast</TabsTrigger>
        </TabsList>
        
        <TabsContent value="profit-loss">
          {renderProfitLossTable()}
        </TabsContent>
        
        <TabsContent value="cash-flow">
          {renderCashFlowTable()}
        </TabsContent>
        
        <TabsContent value="operational-costs">
          {renderOperationalCostsTable()}
        </TabsContent>
        
        <TabsContent value="capex">
          {renderCapExTable()}
        </TabsContent>
        
        <TabsContent value="budget-forecast">
          {renderBudgetForecastTable()}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default DataTables;
